import type { Step } from 'react-joyride';

import { PageTitles, TourClasses } from '@revelio/core';

import { resolveNavSectionCopy } from './tour-copy';

export enum TourPhase {
  Intro = 'intro',
  /** One per rendered control-panel section; `navSection` says which. */
  ControlPanelSection = 'control_panel_section',
  PageTabs = 'page_tabs',
  Entities = 'entities',
  Filters = 'filters',
  AiChat = 'ai_chat',
}

export interface TourStepData {
  phase: TourPhase;
  /** Small uppercase label above the title. */
  kicker?: string;
  /** Renders the wide opening card (headline, lede, coverage figures). */
  isIntro?: boolean;
  /** Nav section this step expands, for ControlPanelSection steps. */
  navSection?: string;
  /**
   * Selectors that must be present and visible before Next is enabled. Used
   * where the next step's target only mounts after the page's data resolves.
   */
  waitForTargets?: readonly string[];
}

export function getTourStepData(
  step: Pick<Step, 'data'>
): TourStepData | undefined {
  const payload = step.data;
  if (typeof payload !== 'object' || payload === null) {
    return undefined;
  }
  if (!('phase' in payload)) {
    return undefined;
  }
  const candidate = (payload as TourStepData).phase;
  const isKnownPhase = Object.values(TourPhase).some(
    (phase) => phase === candidate
  );
  return isKnownPhase ? (payload as TourStepData) : undefined;
}

const CONTROL_PANEL_KICKER = 'Control panel';

const ENTITY_PANEL_SELECTOR = `.${TourClasses.TOUR_TRACKING_CLASS}`;
const FILTER_MENU_SELECTOR = `.${TourClasses.TOUR_FILTER_MENU_CLASS}`;

/**
 * The closing step spotlights the assistant FAB *and* the open chat drawer
 * together. Joyride highlights a single element, so the tour renders an
 * invisible box spanning both and points the step at it (see `AiSpotlightAnchor`
 * in `tour.tsx`).
 */
export const AI_SPOTLIGHT_ANCHOR_ID = 'rl-tour-ai-spotlight';

/** A control-panel section as the rail rendered it: its name and child links. */
export interface RenderedNavSection {
  name: string;
  childNames: readonly string[];
}

export interface TourOptions {
  /**
   * Whether the global AI assistant is available. Without the FAB and drawer
   * the closing step would spotlight an empty corner — it's dropped instead.
   */
  assistantEnabled: boolean;
}

/**
 * Builds the tour from the control-panel sections the user can actually see.
 *
 * `sections` comes from the rendered rail, so a user without Reports (or
 * Discovery, or Data) never gets that step and the progress rail is shorter —
 * rather than a step describing a section they can't open. Applications is a
 * step at all only when the user has an enrichment tool (see
 * `resolveNavSectionCopy`), even though its rail section always renders.
 */
export function buildTourSteps(
  sections: readonly RenderedNavSection[],
  { assistantEnabled }: TourOptions
): Step[] {
  const sectionSteps: Step[] = sections.flatMap((section) => {
    const content = resolveNavSectionCopy(section.name, section.childNames);
    if (content === null) {
      return [];
    }
    return [
      {
        title: section.name,
        content,
        target: `[data-tour-nav-section="${section.name}"]`,
        placement: 'right' as const,
        disableBeacon: true,
        spotlightPadding: 4,
        data: {
          phase: TourPhase.ControlPanelSection,
          kicker: CONTROL_PANEL_KICKER,
          navSection: section.name,
        } satisfies TourStepData,
      },
    ];
  });

  const introStep: Step = {
    // Title and body live in the tooltip's intro card, not here.
    title: '',
    content: '',
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    data: {
      phase: TourPhase.Intro,
      isIntro: true,
    } satisfies TourStepData,
  };

  // The company-page walk lives on a company analytics page: the tour
  // navigates to /company/compositions to show it. A user with no analytics
  // access has no such page, so the phase is dropped and the tour ends on their
  // last control-panel section — never navigating somewhere they can't open.
  const hasAnalytics = sections.some(
    (section) => section.name === PageTitles.DASHBOARD_NAV
  );

  if (!hasAnalytics) {
    return [introStep, ...sectionSteps];
  }

  const companyPageSteps: Step[] = [
    {
      title: 'Entities',
      content: 'Select entities to analyze.',
      target: ENTITY_PANEL_SELECTOR,
      placement: 'right-start',
      disableBeacon: true,
      data: {
        phase: TourPhase.Entities,
        kicker: 'Company page',
        // This is the first company-page step, so it navigates in (see the
        // Entities case in applyStepChoreography). The entity panel and filter
        // menu mount with the page's data — gate Next until both exist so the
        // Filters and Views steps that follow always have a target.
        waitForTargets: [ENTITY_PANEL_SELECTOR, FILTER_MENU_SELECTOR],
      } satisfies TourStepData,
    },
    {
      title: 'Filters',
      content:
        'Filter each entity by role, seniority, region and other employee characteristics.',
      target: FILTER_MENU_SELECTOR,
      placement: 'right-start',
      disableBeacon: true,
      data: {
        phase: TourPhase.Filters,
        kicker: 'Company page',
      } satisfies TourStepData,
    },
    {
      title: 'Views',
      content: 'Each tab is a different lens to view each entity.',
      target: `.${TourClasses.TOUR_ANALYTICS_VIEW_TABS_CLASS}`,
      placement: 'bottom',
      disableBeacon: true,
      data: {
        phase: TourPhase.PageTabs,
        kicker: 'Company page',
      } satisfies TourStepData,
    },
  ];

  // The closing assistant step only works when the FAB and drawer render — i.e.
  // when the global assistant feature is on. With it off, drop the step rather
  // than spotlight an empty corner and open a drawer that never appears.
  const assistantStep: Step[] = assistantEnabled
    ? [
        {
          title: 'Ask instead of clicking',
          content:
            'Put a workforce question in plain language. The assistant can pull a figure, explain a chart, or build a page for you.',
          // Spans the FAB + open drawer (see AI_SPOTLIGHT_ANCHOR_ID).
          target: `#${AI_SPOTLIGHT_ANCHOR_ID}`,
          placement: 'left-end',
          disableBeacon: true,
          spotlightPadding: 6,
          // No overlay override here: the anchor spans the drawer as well as
          // the FAB, so the drawer sits inside the spotlight hole rather than
          // behind the overlay. Lightening the overlay to keep it legible only
          // made the closing step read paler than every step before it.
          data: {
            phase: TourPhase.AiChat,
            kicker: 'AI assistant',
          } satisfies TourStepData,
        },
      ]
    : [];

  return [introStep, ...sectionSteps, ...companyPageSteps, ...assistantStep];
}
